import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import {
	Copy,
	Check,
	UserRound,
	ChevronDown,
	ChevronUp,
	ArrowDownLeft,
	ArrowUpRight,
	ExternalLink,
	RefreshCw,
	CheckCircle2,
} from "lucide-react";
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	Avatar,
	AvatarFallback,
	AvatarImage,
	Badge,
	Separator,
	AttachmentGallery,
	cn,
} from "@hotelise/ui";
import type { GalleryFile } from "@hotelise/ui";

import FileUploadService from "services/storage/fileUpload";

import { ReportTableItemWithTransfer } from "interfaces/reports";

// ── Types ──────────────────────────────────────────────────────────────

interface ReservationDetailsPanelProps {
	reservation: ReportTableItemWithTransfer | null;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

// ── Helpers ────────────────────────────────────────────────────────────

const CURRENCY_SYMBOLS: Record<string, string> = {
	USD: "$",
	GBP: "£",
	AMD: "֏",
	EUR: "€",
};

const money = (n: number, currency: string) => {
	if (n === 0) return `${CURRENCY_SYMBOLS[currency] || currency}0`;
	try {
		return new Intl.NumberFormat(undefined, {
			style: "currency",
			currency,
		}).format(n);
	} catch {
		return `${CURRENCY_SYMBOLS[currency] || currency}${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
	}
};

const statusBadgeClass = (s: string) => {
	switch (s) {
		case "accepted":
			return "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800";
		case "cancelled":
			return "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800";
		case "pending":
		case "expired":
			return "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800";
		default:
			return "";
	}
};

// ── Copyable text ──────────────────────────────────────────────────────

function CopyableText({
	text,
	className,
}: {
	text: string;
	className?: string;
}) {
	const [copied, setCopied] = useState(false);
	const copy = async (e: React.MouseEvent) => {
		e.stopPropagation();
		try {
			await navigator.clipboard.writeText(text);
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		} catch {
			// Clipboard API may be unavailable in non-secure contexts
		}
	};
	return (
		<span className={cn("inline-flex items-center gap-1.5 group", className)}>
			<span className="font-mono text-sm">{text}</span>
			<button
				type="button"
				onClick={copy}
				aria-label={`Copy ${text}`}
				className="text-muted-foreground hover:text-foreground transition-colors"
			>
				{copied ? (
					<Check className="h-3.5 w-3.5 text-emerald-500" />
				) : (
					<Copy className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
				)}
			</button>
		</span>
	);
}

// ── Detail row ─────────────────────────────────────────────────────────

function DetailRow({
	label,
	value,
	highlight,
	className,
}: {
	label: string;
	value: React.ReactNode;
	highlight?: boolean;
	className?: string;
}) {
	return (
		<div
			className={cn(
				"flex items-center justify-between py-1.5",
				className
			)}
		>
			<span
				className={cn(
					"text-sm",
					highlight
						? "text-foreground font-medium"
						: "text-muted-foreground"
				)}
			>
				{label}
			</span>
			<span
				className={cn(
					"text-sm tabular-nums",
					highlight ? "font-semibold" : "font-medium"
				)}
			>
				{value}
			</span>
		</div>
	);
}

// ── Guest count icons ──────────────────────────────────────────────────

function GuestIcons({ adults }: { adults: number }) {
	return (
		<div className="flex items-center gap-3 text-sm">
			<span
				className="flex items-center gap-1 text-muted-foreground"
				title={`${adults} adult${adults !== 1 ? "s" : ""}`}
			>
				<UserRound className="h-4 w-4" />
				<span className="font-medium text-foreground">{adults}</span>
			</span>
		</div>
	);
}

// ── Main component ─────────────────────────────────────────────────────

const ReservationDetailsPanel = ({
	reservation,
	open,
	onOpenChange,
}: ReservationDetailsPanelProps) => {
	const navigate = useNavigate();
	const [financialsExpanded, setFinancialsExpanded] = useState(false);

	// Cleaning state — derived directly from _item.cleaningData (no extra API call needed)
	const cleaningProject = reservation?._item?.cleaningData ?? null;
	const [cleaningGalleryOpen, setCleaningGalleryOpen] = useState(false);
	const [cleaningGalleryIndex, setCleaningGalleryIndex] = useState(0);
	const [cleaningGalleryFiles, setCleaningGalleryFiles] = useState<GalleryFile[]>([]);

	// Sign S3 URLs for cleaning images whenever the reservation/cleaningData changes
	useEffect(() => {
		if (!cleaningProject?.images?.length) {
			setCleaningGalleryFiles([]);
			return;
		}

		let cancelled = false;
		const loadSignedUrls = async () => {
			const files = await Promise.all(
				cleaningProject.images!.map(async (img, index) => {
					try {
						const signedUrl = await FileUploadService.getSignedUrl(img.url);
						return {
							id: `cleaning-${index}`,
							name: `Cleaning photo ${index + 1}`,
							url: signedUrl,
							size: 0,
							type: 'image/jpeg',
						} as GalleryFile;
					} catch (error) {
						console.error('Failed to get signed URL for cleaning image:', error);
						return null;
					}
				}),
			);
			if (!cancelled) {
				setCleaningGalleryFiles(files.filter(Boolean) as GalleryFile[]);
			}
		};

		loadSignedUrls();
		return () => { cancelled = true; };
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [reservation?._item?.uuid, open]);

	if (!reservation) return null;

	const r = reservation._item;
	const transfer = reservation._transferDetails;
	const property = reservation.property;
	const currency = r.currency || "USD";

	const guestName =
		`${r.guest?.first_name || ""} ${r.guest?.last_name || ""}`.trim() ||
		"Guest";
	const guestInitials = `${r.guest?.first_name?.[0] || ""}${r.guest?.last_name?.[0] || ""}`;
	const guestPhoto = r.guest?.picture_url || "";

	const propertyName = property?.name || "Unknown";
	const propertyThumbnail = property?.picture || "";
	const propertyAddress = property?.address?.display || "";
	const propertyId = property?.id || "";

	const toOwner = transfer._accountHolder === 1;

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent className="w-full sm:max-w-lg overflow-y-auto p-0 flex flex-col">
				<SheetHeader className="sr-only">
					<SheetTitle>Reservation Details</SheetTitle>
				</SheetHeader>

				<div className="flex-1 overflow-y-auto">
					{/* ─── Hero Section ──────────────────────────────────────── */}
					<div className="flex flex-col items-center px-6 pt-6 pb-4">
						<Avatar className="h-20 w-20 rounded-full ring-2 ring-border">
							<AvatarImage
								src={guestPhoto}
								alt={guestName}
								className="object-cover"
							/>
							<AvatarFallback className="text-lg">
								{guestInitials}
							</AvatarFallback>
						</Avatar>
						<h2 className="text-lg font-semibold mt-3">
							{guestName}
						</h2>

						{/* Dates bar */}
						<div className="flex items-center gap-6 mt-4 text-center">
							<div>
								<p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
									Check In
								</p>
								<p className="text-sm font-semibold mt-0.5">
									{format(
										new Date(r.start_date),
										"d MMM, yyyy"
									)}
								</p>
							</div>
							<div>
								<p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
									Check Out
								</p>
								<p className="text-sm font-semibold mt-0.5">
									{format(
										new Date(r.end_date),
										"d MMM, yyyy"
									)}
								</p>
							</div>
							<div>
								<p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
									Nights
								</p>
								<p className="text-sm font-semibold mt-0.5">
									{r.nights}
								</p>
							</div>
						</div>
					</div>

					<Separator />

					{/* ─── Guest Info ────────────────────────────────────────── */}
					<section className="px-6 py-4">
						<h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
							Guest Info
						</h3>
						<DetailRow label="Full Name" value={guestName} />
						{r.adults > 0 && (
							<DetailRow
								label="Guests"
								value={<GuestIcons adults={r.adults} />}
							/>
						)}
					</section>

					<Separator />

					{/* ─── Property Details ──────────────────────────────────── */}
					<section className="px-6 py-4">
						<h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
							Property Details
						</h3>
						<DetailRow
							label="Title"
							value={
								<div className="flex items-center gap-2">
									{propertyThumbnail && (
										<Avatar className="h-5 w-5 rounded-md">
											<AvatarImage
												src={propertyThumbnail}
												className="object-cover"
											/>
											<AvatarFallback className="rounded-md text-[8px]">
												{propertyName
													.slice(0, 2)
													.toUpperCase()}
											</AvatarFallback>
										</Avatar>
									)}
									<button
										type="button"
										className="hover:underline text-primary font-medium flex items-center gap-1"
										onClick={() => {
											onOpenChange(false);
											navigate(
												`/properties/${propertyId}`
											);
										}}
									>
										{propertyName}
										<ExternalLink className="h-3 w-3 opacity-60" />
									</button>
								</div>
							}
						/>
						{propertyAddress && (
							<DetailRow
								label="Address"
								value={
									<span className="text-right max-w-[200px] truncate">
										{propertyAddress}
									</span>
								}
							/>
						)}
						<DetailRow
							label="Channel"
							value={
								<Badge
									variant="secondary"
									className="text-[10px]"
								>
									{r.channel}
								</Badge>
							}
						/>
						<DetailRow
							label="Reservation Code"
							value={<CopyableText text={r.code} />}
						/>
						<DetailRow
							label="Status"
							value={
								<Badge
									variant="outline"
									className={cn(
										"text-[10px]",
										statusBadgeClass(r.status)
									)}
								>
									{r.status}
								</Badge>
							}
						/>
					</section>

					<Separator />

					{/* ─── Payment Details ───────────────────────────────────── */}
					<section className="px-6 py-4">
						<h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
							Payment Details
						</h3>

						<DetailRow
							label="Payout"
							value={money(r.payout_price, currency)}
							highlight
						/>
						<Separator className="my-1.5" />

						<DetailRow
							label="Hotelise Help"
							value={money(r.hotelise_help, currency)}
						/>
						<DetailRow
							label="Host Service Fee"
							value={money(r.host_service_fee, currency)}
						/>
						{r.cleaning > 0 && (
							<DetailRow
								label="Cleaning Fee"
								value={money(r.cleaning, currency)}
							/>
						)}

						<Separator className="my-1.5" />

						<DetailRow
							label="Owner Income"
							value={money(r.owner_income, currency)}
							highlight
						/>

						{/* Transfer */}
						<div className="flex items-center justify-between py-1.5">
							<span className="text-sm font-medium flex items-center gap-1.5">
								Transfer
								{toOwner ? (
									<ArrowDownLeft className="h-3.5 w-3.5 text-emerald-500" />
								) : (
									<ArrowUpRight className="h-3.5 w-3.5 text-amber-500" />
								)}
							</span>
							<span
								className={cn(
									"text-sm font-semibold tabular-nums",
									toOwner
										? "text-emerald-600 dark:text-emerald-400"
										: "text-amber-600 dark:text-amber-400"
								)}
							>
								{money(Math.abs(r.owner_income), currency)}
							</span>
						</div>

						{/* Expandable full details */}
						<button
							type="button"
							onClick={() =>
								setFinancialsExpanded(!financialsExpanded)
							}
							className="flex items-center gap-1.5 mt-2 text-xs text-muted-foreground hover:text-foreground transition-colors w-full"
						>
							{financialsExpanded ? (
								<ChevronUp className="h-3.5 w-3.5" />
							) : (
								<ChevronDown className="h-3.5 w-3.5" />
							)}
							{financialsExpanded ? "Hide" : "Show"} full
							breakdown
						</button>

						{financialsExpanded && (
							<div className="mt-2 pt-2 border-t border-dashed space-y-0">
								<DetailRow
									label="Accommodation"
									value={money(r.accommodation, currency)}
								/>
								<DetailRow
									label="Net Accommodation"
									value={money(r.net_accomdation, currency)}
								/>
							</div>
						)}
					</section>

					{/* ─── Cleaning ────────────────────────────────────────── */}
					{cleaningProject && (
						<>
							<Separator />
							<section className="px-6 py-4 pb-8">
								<div className="flex items-center gap-2 mb-2">
									<h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
										Cleaning
									</h3>
									{cleaningProject.status === 'started' ? (
										<span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
											<RefreshCw className="h-3.5 w-3.5 animate-spin" />
											In progress
										</span>
									) : (
										<span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
											<CheckCircle2 className="h-3.5 w-3.5" />
											Done
										</span>
									)}
								</div>

								{cleaningProject.cleaner_name && (
									<div className="flex items-center justify-between py-1.5">
										<span className="text-sm text-muted-foreground">Cleaner</span>
										<span className="text-sm font-medium">{cleaningProject.cleaner_name}</span>
									</div>
								)}

								{cleaningProject.started_at && (
									<div className="flex items-center justify-between py-1.5">
										<span className="text-sm text-muted-foreground">Started</span>
										<span className="text-sm font-medium">{format(new Date(cleaningProject.started_at), 'd MMM yyyy, HH:mm')}</span>
									</div>
								)}

								{cleaningProject.completed_at && (
									<div className="flex items-center justify-between py-1.5">
										<span className="text-sm text-muted-foreground">Completed</span>
										<span className="text-sm font-medium">{format(new Date(cleaningProject.completed_at), 'd MMM yyyy, HH:mm')}</span>
									</div>
								)}

								{/* Photo thumbnails — overlapping, click to open gallery */}
								{cleaningGalleryFiles.length > 0 && (
									<div className="flex -space-x-1.5 mt-3">
										{cleaningGalleryFiles.slice(0, 6).map((ph, i) => (
											<button
												key={ph.id}
												type="button"
												className="cursor-pointer hover:opacity-80 hover:z-10 transition-all"
												style={{ zIndex: 6 - i }}
												onClick={() => {
													setCleaningGalleryIndex(i);
													setCleaningGalleryOpen(true);
												}}
											>
												<img
													src={ph.url}
													alt={ph.name}
													className="h-8 w-8 rounded-md border-2 border-background object-cover"
												/>
											</button>
										))}
										{cleaningGalleryFiles.length > 6 && (
											<button
												type="button"
												className="h-8 w-8 rounded-md border-2 border-background bg-muted flex items-center justify-center text-[10px] font-semibold text-muted-foreground cursor-pointer hover:opacity-80 transition-opacity"
												style={{ zIndex: 0 }}
												onClick={() => {
													setCleaningGalleryIndex(6);
													setCleaningGalleryOpen(true);
												}}
											>
												+{cleaningGalleryFiles.length - 6}
											</button>
										)}
									</div>
								)}

								{/* Loading thumbnails skeleton */}
								{cleaningProject.images && cleaningProject.images.length > 0 && cleaningGalleryFiles.length === 0 && (
									<div className="flex gap-1.5 mt-3">
										{[1, 2, 3].map((i) => (
											<div key={i} className="h-8 w-8 rounded-md bg-muted animate-pulse" />
										))}
									</div>
								)}
							</section>
						</>
					)}

				</div>

				{/* Cleaning photos gallery — rendered inside SheetContent to avoid Fragment */}
				{cleaningGalleryFiles.length > 0 && (
					<AttachmentGallery
						open={cleaningGalleryOpen}
						onOpenChange={setCleaningGalleryOpen}
						files={cleaningGalleryFiles}
						title="Cleaning Photos"
						initialIndex={cleaningGalleryIndex}
					/>
				)}
			</SheetContent>
		</Sheet>
	);
};

export default ReservationDetailsPanel;
